(() => {
  'use strict';

  const PRODUCT_INFO_SELECTOR = 'product-info[id^="MainProduct"]';
  const PREVIEW_BADGE_SELECTOR = '.jdgm-preview-badge';
  const REVIEW_WIDGET_SELECTOR = '.jdgm-review-widget[data-entry-point]';
  const initialProductInfo = document.querySelector(PRODUCT_INFO_SELECTOR);

  const state = {
    generation: 0,
    manager: null,
    managerClassPromise: null,
  };

  function isCurrent(productInfo, generation) {
    return (
      generation === state.generation &&
      productInfo.isConnected &&
      document.querySelector(PRODUCT_INFO_SELECTOR) === productInfo
    );
  }

  function unmountManagedWidget() {
    const app = state.manager?.widget?.app;

    if (app && typeof app.unmount === 'function') {
      try {
        app.unmount();
      } catch (error) {
        console.warn('[Casemeta] Judge.me widget cleanup failed.', error);
      }
    }

    state.manager = null;
  }

  function refreshJudgeMeCaches() {
    if (!window.jdgm?.$) return;

    window.jdgm.caches ||= {};
    window.jdgm.caches.$widgetWithReviews = window.jdgm.$(
      '.jdgm-review-widget, .jdgm-revs-tab, .jdgm-all-reviews-widget'
    );
    window.jdgm.caches.$revWidgets = window.jdgm.$('.jdgm-review-widget, [data-widget="review"]');
    window.jdgm.caches.$prevBadges = window.jdgm.$(PREVIEW_BADGE_SELECTOR);
  }

  function waitForJudgeMe(timeout = 7000) {
    const startedAt = Date.now();

    return new Promise((resolve, reject) => {
      const check = () => {
        if (window.jdgm?.$ && typeof window.jdgm.customizeBadges === 'function') {
          resolve();
          return;
        }

        if (Date.now() - startedAt >= timeout) {
          reject(new Error('Judge.me core did not become ready in time.'));
          return;
        }

        window.setTimeout(check, 50);
      };

      check();
    });
  }

  function getReviewCount(badge) {
    const count = Number(badge?.querySelector('.jdgm-prev-badge')?.dataset.numberOfReviews || 0);
    return Number.isFinite(count) ? count : 0;
  }

  function applyBadgeVisibility(badge) {
    const shouldHideEmpty = Boolean(window.jdgmSettings?.hide_badge_preview_if_no_reviews);

    if (shouldHideEmpty && getReviewCount(badge) <= 0) {
      badge.style.display = 'none';
    } else {
      badge.style.removeProperty('display');
    }
  }

  function initializePreviewBadge(badge) {
    refreshJudgeMeCaches();
    window.jdgm.customizeBadges();
    applyBadgeVisibility(badge);
  }

  function findReviewData(productId) {
    if (!window.jdgm) return null;

    const marker = new RegExp(`jdgm\\.data\\.reviewWidget\\[\\s*${productId}\\s*\\]\\s*=\\s*`);
    const dataScript = Array.from(document.scripts).find((script) => marker.test(script.textContent || ''));

    if (dataScript) {
      const source = dataScript.textContent || '';
      const match = source.match(marker);

      if (match?.index !== undefined) {
        const json = source
          .slice(match.index + match[0].length)
          .trim()
          .replace(/;\s*$/, '');

        try {
          const reviewData = JSON.parse(json);
          window.jdgm.data ||= {};
          window.jdgm.data.reviewWidget ||= {};
          window.jdgm.data.reviewWidget[productId] = reviewData;
          return reviewData;
        } catch (error) {
          console.warn('[Casemeta] Judge.me product data could not be parsed.', error);
        }
      }
    }

    return window.jdgm.data?.reviewWidget?.[productId] || null;
  }

  function getReviewWidgetManagerClass() {
    if (state.managerClassPromise) return state.managerClassPromise;

    state.managerClassPromise = (async () => {
      const entryScript = Array.from(document.scripts).find((script) => {
        if (!script.src) return false;

        try {
          return new URL(script.src).pathname.endsWith('/assets/review_widget.js');
        } catch (error) {
          return false;
        }
      });

      if (!entryScript) {
        throw new Error('Judge.me review widget entry script was not found.');
      }

      const response = await fetch(entryScript.src, {
        cache: 'force-cache',
        credentials: 'omit',
      });

      if (!response.ok) {
        throw new Error(`Judge.me entry script request failed (${response.status}).`);
      }

      const source = await response.text();
      const modulePath = source.match(/from\s*["'](\.\/ReviewWidgetManager-[^"']+\.js)["']/)?.[1];

      if (!modulePath) {
        throw new Error('Judge.me review widget manager module was not found.');
      }

      const module = await import(new URL(modulePath, entryScript.src).href);

      if (typeof module.ReviewWidgetManager !== 'function') {
        throw new Error('Judge.me review widget manager is unavailable.');
      }

      return module.ReviewWidgetManager;
    })().catch((error) => {
      state.managerClassPromise = null;
      throw error;
    });

    return state.managerClassPromise;
  }

  function showLegacyReviewWidget(reviewWidget, legacyBackup = null) {
    let legacy = reviewWidget.querySelector('.jdgm-legacy-widget-content');

    if (!legacy && legacyBackup) {
      reviewWidget.replaceChildren(legacyBackup);
      legacy = legacyBackup;
    }

    if (!legacy) return;

    reviewWidget.classList.remove('jdgm-widget-revamp', 'jdgm--done-setup-widget');
    legacy.style.removeProperty('display');
    legacy.querySelector('.jdgm-temp-hiding-style')?.remove();
    legacy.querySelector('.jdgm-rev-widg')?.style.removeProperty('display');

    if (window.jdgm?.$ && typeof window.jdgm.initializeWidgets === 'function') {
      refreshJudgeMeCaches();
      window.jdgm.initializeWidgets(window.jdgm.$(reviewWidget.parentElement || document.body));
    }
  }

  async function initializeReviewWidget(reviewWidget, productId, productInfo, generation) {
    const reviewData = findReviewData(productId);
    const legacyBackup = reviewWidget.querySelector('.jdgm-legacy-widget-content')?.cloneNode(true) || null;

    if (!reviewData) {
      showLegacyReviewWidget(reviewWidget, legacyBackup);
      return;
    }

    try {
      const ReviewWidgetManager = await getReviewWidgetManagerClass();

      if (!isCurrent(productInfo, generation) || !reviewWidget.isConnected) return;

      const manager = new ReviewWidgetManager(reviewWidget);
      await manager.initialize();

      if (!isCurrent(productInfo, generation) || !reviewWidget.isConnected) {
        manager.widget?.app?.unmount?.();
        return;
      }

      state.manager = manager;
      refreshJudgeMeCaches();
    } catch (error) {
      if (!isCurrent(productInfo, generation) || !reviewWidget.isConnected) return;

      console.warn('[Casemeta] Judge.me dynamic widget fallback activated.', error);
      showLegacyReviewWidget(reviewWidget, legacyBackup);
    }
  }

  function initializeBadgeFallback(badge, reviewWidget) {
    const count = getReviewCount(badge);

    if (count <= 0) {
      badge.style.display = 'none';
      badge.classList.add('jdgm--done-setup');
      return;
    }

    const preview = badge.querySelector('.jdgm-prev-badge');
    const rating = Number(preview?.dataset.averageRating || 0);
    const text = badge.querySelector('.jdgm-prev-badge__text');

    if (text) text.textContent = `${rating.toFixed(1)} · ${count} değerlendirme`;

    badge.style.removeProperty('display');
    badge.classList.add('jdgm-preview-badge--with-link', 'jdgm--done-setup');
    badge.addEventListener('click', (event) => {
      event.preventDefault();
      reviewWidget?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  async function reinitializeJudgeMe(productInfo, generation) {
    if (!isCurrent(productInfo, generation)) return;

    unmountManagedWidget();

    const badge = productInfo.querySelector(PREVIEW_BADGE_SELECTOR);
    const productId = String(productInfo.dataset.productId || badge?.dataset.id || '');
    const reviewWidget = document.querySelector(REVIEW_WIDGET_SELECTOR);

    if (!badge || !productId || reviewWidget?.dataset.productId !== productId) return;

    try {
      await waitForJudgeMe();

      if (!isCurrent(productInfo, generation)) return;

      findReviewData(productId);
      initializePreviewBadge(badge);
      await initializeReviewWidget(reviewWidget, productId, productInfo, generation);
    } catch (error) {
      if (!isCurrent(productInfo, generation)) return;

      console.warn('[Casemeta] Judge.me dynamic initialization fallback activated.', error);
      initializeBadgeFallback(badge, reviewWidget);
      showLegacyReviewWidget(reviewWidget);
    }
  }

  document.addEventListener('product-info:loaded', (event) => {
    const productInfo = event.target;

    if (!(productInfo instanceof HTMLElement) || !productInfo.matches(PRODUCT_INFO_SELECTOR)) return;
    if (productInfo === initialProductInfo) return;

    const badge = productInfo.querySelector(PREVIEW_BADGE_SELECTOR);
    const reviewWidget = document.querySelector(REVIEW_WIDGET_SELECTOR);

    if (badge?.classList.contains('jdgm--done-setup') && reviewWidget?.classList.contains('jdgm-widget-revamp')) {
      return;
    }

    productInfo.classList.add('cm-judgeme-rebinding');
    const generation = ++state.generation;
    window.setTimeout(() => reinitializeJudgeMe(productInfo, generation), 0);
  });
})();
